import { useState } from 'react';

export default function HomePage() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    book: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.address || !formData.book) {
      alert('모든 항목을 입력해주세요.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        name: formData.name,
        phone: formData.phone,
        address: formData.address,
        book: formData.book
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d57qu293kamldd155s20', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', address: '', book: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-stone-100">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-6 py-20 bg-gradient-to-br from-emerald-900 via-emerald-800 to-stone-800">
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/e21fa42b8556b28f0535bc5a890dafaa.jpg')] bg-cover bg-center opacity-20"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <img 
            src="https://static.readdy.ai/image/978f3255162089bd9a3c930a82c95049/1d17c2b718a80dc837f4e22bff4ccca2.png" 
            alt="행크연구소" 
            className="w-32 h-32 mx-auto mb-12 object-contain"
          />
          
          <div className="space-y-8 text-stone-50 leading-relaxed text-base">
            <p className="text-xl font-medium">행크연구소 후원자님,</p>
            
            <p>대통령 비상계엄과 탄핵이라는 풍랑 속에서 시작한 2025년이<br />어느덧 마무리되고 있습니다.</p>
            
            <p>그 어느 때보다 대한민국과 다음 세대를 위해<br />많은 성도들이 눈물로 기도한 한 해였습니다.</p>
            
            <p>나라가 어려운 시간을 지나고 있지만,<br />함께 기도하며 뜻을 나눌 수 있는 동역자가 계심에<br />진심으로 감사한 마음입니다.</p>
            
            <p>이 나라와 민족을 향한 후원자님의 헌신과 기도에<br />하나님은 반드시 응답하실 것입니다.</p>
            
            <p>모든 것을 합력하여 선을 이루시는 하나님께서<br />역사의 시곗바늘을 돌리시고 있음을 믿습니다.</p>
            
            <p>대한민국을 향한 하나님의 부르심이<br />반드시 이루어질 것을 믿습니다.</p>
            
            <p>우리 안의 우상이 깨어지고<br />오직 하나님만 의지하도록<br />우리의 믿음을 새롭게 하시길 기도합니다.</p>
            
            <p className="text-lg font-medium pt-4">주 안에서 따뜻한 연말 되시길 바랍니다.</p>
            
            <p className="text-2xl font-bold pt-6">"하나님이 보우하사, 우리나라 만세!"</p>
            
            <p className="text-lg pt-8">행크연구소 드림.</p>
          </div>
        </div>
      </section>

      {/* 2025년 활동 하이라이트 */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4 text-stone-800">2025년 활동 하이라이트</h2>
          <p className="text-center text-stone-600 mb-16 text-base">후원자님의 소중한 후원으로 이루어낸 성과입니다</p>
          
          {/* 유튜브 성과 */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* 행크연구소 채널 */}
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-red-600 rounded-full">
                  <i className="ri-youtube-fill text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-stone-800">행크연구소 채널</h3>
              </div>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-stone-600 text-sm">업로드 영상</span>
                  <span className="text-3xl font-bold text-red-600">169개</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-stone-600 text-sm">누적 조회수</span>
                  <span className="text-3xl font-bold text-red-600">1,053,627회</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-stone-600 text-sm">구독자 증가</span>
                  <span className="text-3xl font-bold text-red-600">+14,000명</span>
                </div>
              </div>
              
              <a 
                href="https://www.youtube.com/@hanklab" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block w-full bg-red-600 text-white text-center py-3 rounded-lg font-medium hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                채널 바로가기
              </a>
            </div>

            {/* 행크뉴스 채널 */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-full">
                  <i className="ri-youtube-fill text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-stone-800">행크뉴스 채널</h3>
              </div>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-stone-600 text-sm">업로드 영상</span>
                  <span className="text-3xl font-bold text-blue-600">20개</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-stone-600 text-sm">누적 조회수</span>
                  <span className="text-3xl font-bold text-blue-600">3,298,396회</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-stone-600 text-sm">구독자 증가</span>
                  <span className="text-3xl font-bold text-blue-600">+19,000명</span>
                </div>
              </div>
              
              <a 
                href="https://www.youtube.com/@hanknews" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block w-full bg-blue-600 text-white text-center py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                채널 바로가기
              </a>
            </div>
          </div>

          {/* 칼럼 및 기고 */}
          <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-amber-600 rounded-full">
                <i className="ri-article-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-stone-800">칼럼 및 기고 활동</h3>
            </div>
            
            <div className="mb-4">
              <p className="text-stone-700 mb-4 text-base">성경적 정치관·기독교 세계관 관련 칼럼 <strong className="text-amber-700 text-2xl">총 10건</strong></p>
              <div className="flex flex-wrap gap-3">
                <span className="px-4 py-2 bg-white rounded-full text-stone-700 text-sm font-medium">크리스천투데이</span>
                <span className="px-4 py-2 bg-white rounded-full text-stone-700 text-sm font-medium">복음기도신문</span>
                <span className="px-4 py-2 bg-white rounded-full text-stone-700 text-sm font-medium">월드뷰</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2025년 주요 프로젝트 */}
      <section className="py-24 px-6 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4 text-stone-800">2025년 주요 프로젝트</h2>
          <p className="text-center text-stone-600 mb-16 text-base">말이 아니라 실제 행동으로 보여드렸습니다</p>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* 교회자유수호연대 */}
            <div className="bg-white rounded-lg p-8 shadow-sm hover:shadow-lg transition-shadow border border-stone-200">
              <div className="mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-emerald-600 rounded-lg mb-4">
                  <i className="ri-shield-check-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-stone-800 mb-3">교회자유수호연대 사이트 런칭</h3>
                <a 
                  href="https://church-freedom.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-emerald-600 hover:text-emerald-700 font-medium text-sm cursor-pointer"
                >
                  church-freedom.com →
                </a>
              </div>
              
              <div className="space-y-3 mb-6">
                <p className="text-stone-700 text-sm"><strong>부산 세계로교회 손현보 목사</strong> 구속 반대 서명운동</p>
                <div className="flex items-center gap-2 text-stone-600 text-sm">
                  <i className="ri-calendar-line"></i>
                  <span>구속수감 D+110일 (12/27 기준)</span>
                </div>
                <div className="flex items-center gap-2 text-emerald-600 font-bold text-base">
                  <i className="ri-user-line"></i>
                  <span>서명 7,421명 돌파</span>
                </div>
                <div className="flex items-center gap-2 text-stone-600 text-sm">
                  <i className="ri-chat-3-line"></i>
                  <span>댓글 1,000건 이상</span>
                </div>
              </div>
            </div>

            {/* 위체인지 */}
            <div className="bg-white rounded-lg p-8 shadow-sm hover:shadow-lg transition-shadow border border-stone-200">
              <div className="mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-indigo-600 rounded-lg mb-4">
                  <i className="ri-hand-heart-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-stone-800 mb-3">위체인지 (WE-CHANGE) 플랫폼</h3>
                <a 
                  href="https://we-change.kr" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-700 font-medium text-sm cursor-pointer"
                >
                  we-change.kr →
                </a>
              </div>
              
              <p className="text-stone-700 text-sm">악법 반대를 위한 시민 청원 플랫폼</p>
            </div>

            {/* 그리스도를 위해 투표하라 */}
            <div className="bg-white rounded-lg p-8 shadow-sm hover:shadow-lg transition-shadow border border-stone-200">
              <div className="mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-rose-600 rounded-lg mb-4">
                  <i className="ri-checkbox-circle-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-stone-800 mb-3">그리스도를 위해 투표하라</h3>
                <a 
                  href="https://voteforchrist.kr" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-rose-600 hover:text-rose-700 font-medium text-sm cursor-pointer"
                >
                  voteforchrist.kr →
                </a>
              </div>
              
              <p className="text-stone-700 text-sm">기독교 투표 독려 운동 플랫폼</p>
            </div>

            {/* 세이브코리아 */}
            <div className="bg-white rounded-lg p-8 shadow-sm hover:shadow-lg transition-shadow border border-stone-200">
              <div className="mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-teal-600 rounded-lg mb-4">
                  <i className="ri-flag-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-stone-800 mb-3">세이브코리아 웹사이트 런칭</h3>
              </div>
              
              <p className="text-stone-700 text-sm">대한민국을 지키기 위한 시민운동 플랫폼</p>
            </div>

            {/* VFC인덱스 */}
            <div className="bg-white rounded-lg p-8 shadow-sm hover:shadow-lg transition-shadow border border-stone-200">
              <div className="mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-violet-600 rounded-lg mb-4">
                  <i className="ri-team-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-stone-800 mb-3">행동하는크리스천 기도 네트워크</h3>
                <p className="text-sm text-stone-600 mb-3">(가칭: CIPA)</p>
                <div className="space-y-2">
                  <a 
                    href="https://vfcindex.netlify.app/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block text-violet-600 hover:text-violet-700 font-medium text-sm cursor-pointer"
                  >
                    버전1: vfcindex.netlify.app →
                  </a>
                  <a 
                    href="https://codepen.io/Seonbeom-JANG/full/GgqgdgV" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block text-violet-600 hover:text-violet-700 font-medium text-sm cursor-pointer"
                  >
                    버전2: codepen.io →
                  </a>
                </div>
              </div>
              
              <div className="flex items-center gap-2 text-stone-600 text-sm">
                <span className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-xs font-medium">개발 진행중</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 행크연구소 소개 */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 text-stone-800">행크연구소 소개</h2>
          
          <div className="space-y-8 text-stone-700 leading-relaxed mb-12 text-base text-center">
            <p className="text-xl font-medium text-stone-800">안녕하세요? 행동하는 크리스천, 행크연구소입니다.</p>
            
            <p>성경의 진리가 불법이 되는 시대를 살고 있습니다.<br />반기독교적 법이 제정되면 교회는 급속히 타락합니다.</p>
            
            <p>교회를 지키지 못하면<br />다음 세대에는 신앙의 자유가 사라집니다.</p>
            
            <p className="text-lg font-bold text-emerald-700">기도와 행동으로<br />교회의 가치를 사수하겠습니다.</p>
          </div>

          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg p-10">
            <h3 className="text-2xl font-bold text-center mb-8 text-stone-800">행크연구소가 하는 일</h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-emerald-600 rounded-lg flex-shrink-0">
                  <i className="ri-file-list-3-line text-xl text-white"></i>
                </div>
                <div>
                  <p className="font-medium text-stone-800 text-sm">반기독교적 법·정책 분석 및 대응</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-emerald-600 rounded-lg flex-shrink-0">
                  <i className="ri-book-open-line text-xl text-white"></i>
                </div>
                <div>
                  <p className="font-medium text-stone-800 text-sm">기독교 세계관 연구 및 교육 콘텐츠 제작</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-emerald-600 rounded-lg flex-shrink-0">
                  <i className="ri-shield-star-line text-xl text-white"></i>
                </div>
                <div>
                  <p className="font-medium text-stone-800 text-sm">신앙의 자유 수호 활동</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-emerald-600 rounded-lg flex-shrink-0">
                  <i className="ri-global-line text-xl text-white"></i>
                </div>
                <div>
                  <p className="font-medium text-stone-800 text-sm">온라인 지식 플랫폼 개발</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2026년 후원 요청 */}
      <section className="py-24 px-6 bg-gradient-to-br from-emerald-700 via-emerald-600 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-8">2026년에도 함께해 주세요</h2>
          
          <p className="text-xl leading-relaxed mb-12">
            2026년에도 행크연구소가<br />
            기도하고, 연구하고, 행동할 수 있도록<br />
            변함없는 동행을 부탁드립니다.
          </p>

          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-10 mb-8">
            <h3 className="text-2xl font-bold mb-8">후원 정보</h3>
            
            <div className="space-y-6 text-left max-w-md mx-auto">
              <div className="bg-white/20 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/30 rounded-lg">
                    <i className="ri-bank-card-line text-xl"></i>
                  </div>
                  <h4 className="font-bold text-lg">계좌 후원</h4>
                </div>
                <p className="text-base font-medium">국민은행 821701-01-673838</p>
                <p className="text-sm opacity-90">(행크연구소)</p>
              </div>

              <div className="bg-white/20 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/30 rounded-lg">
                    <i className="ri-heart-line text-xl"></i>
                  </div>
                  <h4 className="font-bold text-lg">정기후원</h4>
                </div>
                <a 
                  href="https://cherry.charity/public/campaign/cmpgnDtlPage/13057" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block mt-2 px-6 py-3 bg-white text-emerald-700 rounded-lg font-medium hover:bg-stone-50 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  정기후원 신청하기 →
                </a>
                <p className="text-sm opacity-90 mt-3">기부금 영수증 발급 가능</p>
              </div>
            </div>
          </div>

          <p className="text-2xl font-bold">기도하고 행동하면, 기적이 일어납니다.</p>
        </div>
      </section>

      {/* 후원자 감사 선물 신청 */}
      <section className="py-24 px-6 bg-stone-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-6 text-stone-800">후원자 감사 선물 신청</h2>
          <p className="text-center text-stone-600 mb-12 text-base">
            행크연구소에서 감사의 마음을 담아<br />
            후원자님께 선물을 보내드립니다.<br />
            아래 두 권의 신간 중 한 권을 선택해 주세요.
          </p>

          {/* 도서 선택 */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* 손현보 목사 저서 */}
            <a 
              href="https://product.kyobobook.co.kr/detail/S000218607814" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white rounded-lg p-8 shadow-sm border-2 border-stone-200 hover:border-emerald-500 transition-colors cursor-pointer block"
            >
              <div className="aspect-[3/4] bg-gradient-to-br from-stone-200 to-stone-300 rounded-lg mb-6 flex items-center justify-center overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/978f3255162089bd9a3c930a82c95049/7434a22164137b72aab582cfab6bd756.jpeg" 
                  alt="손현보 목사 저서" 
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-xl font-bold text-stone-800 mb-3">손현보 목사 저서 (부산세계로교회 담임)</h3>
              <ul className="space-y-2 text-stone-600 text-sm">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1"></i>
                  <span>부산교육감 선거법 위반 혐의로 구속 수감 중 집필</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1"></i>
                  <span>구속 경위와 신앙 고백, 눈물의 간증 수록</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1"></i>
                  <span>불의한 구속수감 110일째 (12/27기준)</span>
                </li>
              </ul>
            </a>

            {/* 김미영 변호사 저서 */}
            <a 
              href="https://product.kyobobook.co.kr/detail/S000218675833" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white rounded-lg p-8 shadow-sm border-2 border-stone-200 hover:border-emerald-500 transition-colors cursor-pointer block"
            >
              <div className="aspect-[3/4] bg-gradient-to-br from-stone-200 to-stone-300 rounded-lg mb-6 flex items-center justify-center overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/978f3255162089bd9a3c930a82c95049/6ef1fb701838d9e31451de3ff756c766.jpeg" 
                  alt="김미영 변호사 저서" 
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-xl font-bold text-stone-800 mb-3">김미영 변호사 저서</h3>
              <ul className="space-y-2 text-stone-600 text-sm">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1"></i>
                  <span>대한민국 자유민주주의 시스템 붕괴를 100가지 팩트로 분석</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1"></i>
                  <span>북한인권 운동에 오랜 시간 헌신한 저자의 연구</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1"></i>
                  <span>완전히 밝혀진 국가 선거시스템 조작의 실체</span>
                </li>
              </ul>
            </a>
          </div>

          {/* 신청 폼 */}
          <form onSubmit={handleSubmit} data-readdy-form id="gift-form" className="bg-white rounded-lg p-10 shadow-sm border border-stone-200">
            <h3 className="text-2xl font-bold text-stone-800 mb-8 text-center">선물 신청서</h3>
            
            <div className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-stone-700 mb-2">
                  이름 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                  placeholder="이름을 입력해주세요"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-stone-700 mb-2">
                  연락처 <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                  placeholder="010-0000-0000"
                />
              </div>

              <div>
                <label htmlFor="address" className="block text-sm font-medium text-stone-700 mb-2">
                  주소 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm resize-none"
                  placeholder="도서를 받으실 주소를 입력해주세요"
                />
                <p className="text-xs text-stone-500 mt-1">{formData.address.length}/500자</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-3">
                  도서 선택 <span className="text-red-500">*</span>
                </label>
                <div className="space-y-3">
                  <label className="flex items-start gap-3 p-4 border-2 border-stone-200 rounded-lg hover:border-emerald-500 cursor-pointer transition-colors">
                    <input
                      type="radio"
                      name="book"
                      value="손현보 목사 저서"
                      checked={formData.book === '손현보 목사 저서'}
                      onChange={handleChange}
                      required
                      className="mt-1 w-5 h-5 text-emerald-600 cursor-pointer"
                    />
                    <span className="text-sm text-stone-700">손현보 목사 저서</span>
                  </label>

                  <label className="flex items-start gap-3 p-4 border-2 border-stone-200 rounded-lg hover:border-emerald-500 cursor-pointer transition-colors">
                    <input
                      type="radio"
                      name="book"
                      value="김미영 변호사 저서"
                      checked={formData.book === '김미영 변호사 저서'}
                      onChange={handleChange}
                      required
                      className="mt-1 w-5 h-5 text-emerald-600 cursor-pointer"
                    />
                    <span className="text-sm text-stone-700">김미영 변호사 저서</span>
                  </label>
                </div>
              </div>

              <div className="bg-stone-50 rounded-lg p-4 text-xs text-stone-600">
                <p className="font-medium mb-2">개인정보 수집 및 이용 안내</p>
                <p>수집 항목: 이름, 연락처, 주소</p>
                <p>수집 목적: 후원자 감사 선물 발송</p>
                <p>보유 기간: 발송 완료 후 3개월</p>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-emerald-700 text-sm">
                  <i className="ri-checkbox-circle-fill mr-2"></i>
                  신청이 완료되었습니다. 감사합니다!
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
                  <i className="ri-error-warning-fill mr-2"></i>
                  신청 중 오류가 발생했습니다. 다시 시도해주세요.
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-emerald-600 text-white py-4 rounded-lg font-bold text-base hover:bg-emerald-700 transition-colors disabled:bg-stone-300 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? '신청 중...' : '선물 신청하기'}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-800 text-stone-300 py-12 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <img 
            src="https://static.readdy.ai/image/978f3255162089bd9a3c930a82c95049/1d17c2b718a80dc837f4e22bff4ccca2.png" 
            alt="행크연구소" 
            className="w-20 h-20 mx-auto mb-6 object-contain opacity-80"
          />
          <p className="text-sm mb-4">행동하는 크리스천, 행크연구소</p>
          <p className="text-xs text-stone-400 mb-6">기도와 행동으로 교회의 가치를 사수합니다</p>
          <div className="text-xs text-stone-500">
            <p>&copy; 2025 행크연구소. All rights reserved.</p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="inline-block mt-3 text-stone-400 hover:text-stone-300 transition-colors cursor-pointer"
            >
              God Save Korea
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
